/**
 * Admin Authentication Context
 * Separate auth context for CRM Admin users
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCrmToken, setCrmToken, clearCrmToken, crmFetch } from '../utils/crmAuth';
import { isCrmEnabled } from '../utils/featureFlags';
import { API_BASE } from '../config/api';

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Bootstrap: Check if admin is authenticated on mount
  useEffect(() => {
    if (!isCrmEnabled()) {
      setLoading(false);
      return;
    }

    const bootstrapAuth = async () => {
      const token = getCrmToken();
      
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // Verify token by calling /me endpoint
        const response = await crmFetch('/api/crm/auth/me');
        
        if (response.ok) {
          const data = await response.json();
          // Verify it's an admin
          if (data.role === 'crm_admin') {
            setUser(data);
          } else {
            // Not an admin - clear token
            clearCrmToken();
            setUser(null);
          }
        } else if (response.status === 401) {
          // Only clear token on 401 (unauthorized) - not on network errors
          clearCrmToken();
          setUser(null);
        } else {
          // Other errors (network, 500, etc.) - keep token, might be temporary
          // Don't clear token on temporary errors
          console.warn('Auth bootstrap failed with non-401 error:', response.status);
        }
      } catch (error) {
        // Network errors or other exceptions - don't clear token
        // Token might still be valid, just network issue
        console.warn('Error bootstrapping admin auth (keeping token):', error);
        // Don't clear token on network errors - allow retry
      } finally {
        setLoading(false);
      }
    };

    bootstrapAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_BASE}/api/crm/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      // Verify user is admin
      if (data.user.role !== 'crm_admin') {
        throw new Error('Admin access required');
      }

      // Store token and user
      setCrmToken(data.access_token);
      setUser(data.user);

      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint (optional - stateless JWT)
      await crmFetch('/api/crm/auth/logout', {
        method: 'POST'
      });
    } catch (error) {
      // Ignore errors - we'll clear token anyway
    } finally {
      clearCrmToken();
      setUser(null);
      navigate('/crm/admin/login');
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
  };

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};
